import { PATH, ROUTE_NAME } from '@/constants'
import { AnalyticsPathEnum, type IUTMParams } from '@setplex/tria-api'
import { attach, createEvent, createStore, sample } from 'effector'
import { and } from 'patronum'
import { selectEpgRegionModel } from '~/features/epg/regions/select'
import { preferencesFeatureModel } from '~/features/preferences'
import { $route } from '~/processes/navigation'
import { analyticsSharedModel } from '~/shared/analytics'
import type { IParamsPageOpenEvent } from '~/shared/analytics/index.h'
import type { SendEventParams } from '~/shared/analytix'
import { getIdFromUrl, T } from '~/shared/helpers'
import * as signInModel from '~/widgets/auth/sign-in/model'
import * as signUpModel from '~/widgets/auth/sign-up/model'
import { getParamsFromUrl, logErrorFx } from '../lib'
import { $readyAllInfo, afterSubscriberIdSettled } from '../session/model'
import { $analytix, $analytixEnabled } from '../wrapper'

const $pageOpenCanBeSent = and($readyAllInfo, $analytixEnabled)

const $isNeedAdditionalPageOpenParams = $route.map((route) => {
  const routeName = route?.routeName || ''
  return (
    routeName === PATH.CREATOR ||
    routeName === PATH.MOVIE ||
    routeName === PATH.HASHTAG ||
    routeName === PATH.FAVORITES ||
    routeName === PATH.TVSHOW ||
    routeName === ROUTE_NAME.MOVIES_CATEGORIES ||
    routeName === ROUTE_NAME.TVSHOWS_CATEGORIES ||
    routeName === ROUTE_NAME.LIVE_CATEGORIES ||
    routeName === ROUTE_NAME.CONTENT_SET
  )
})

type OvpPageOpenPayload = IUTMParams & {
  path?: string
  entityId?: string
  contentTitle?: string
  publisherId?: string
}

const ovpPageOpenPayloadToAnalytix = (
  data: OvpPageOpenPayload
): SendEventParams.PageOpen => ({
  pagePath: data.path ?? '',
  entityId: String(data.entityId ?? ''),
  pageTitle: data.contentTitle ?? '',
  creatorId: String(data.publisherId ?? ''),
  ...(data.utm_source ? { utmSource: String(data.utm_source) } : {}),
  ...(data.utm_medium ? { utmMedium: String(data.utm_medium) } : {}),
  ...(data.utm_campaign ? { utmCampaign: String(data.utm_campaign) } : {}),
  ...(data.utm_term ? { utmTerm: String(data.utm_term) } : {}),
  ...(data.utm_content ? { utmContent: String(data.utm_content) } : {}),
})

const pageOpenFromBeParams = (
  route: { routeName?: string; props?: { titleWithId?: string } } | null,
  paramsPageOpenEvent: IParamsPageOpenEvent | null
): SendEventParams.PageOpen => {
  const {
    contentTitle = '',
    publisherId,
    path = '',
  } = paramsPageOpenEvent || {}
  const entityId =
    paramsPageOpenEvent?.entityId || getIdFromUrl(route?.props?.titleWithId)
  const tab = paramsPageOpenEvent?.contentType || ''
  const pathName = path || route?.routeName || ''

  return ovpPageOpenPayloadToAnalytix({
    path: tab ? `${pathName}/${tab}` : pathName,
    entityId: String(entityId ?? ''),
    publisherId: String(publisherId ?? ''),
    contentTitle,
    ...getParamsFromUrl(),
  })
}

const resetSkipStaleLivePageOpenAfterSignIn = createEvent()

// Successful login closes the popup without closeSignInPopup. Skip Live TV
// until the profile region is known, and Favorites until the tab decision
// rewrites params. Same region on Live TV restores current params; a different
// region waits for $paramsPageOpenEvent to rewrite.
const $skipStaleLivePageOpenAfterSignIn = createStore(false)
  .on(signInModel.signInFx.done, T)
  .reset([
    signInModel.openSignInPopup,
    signInModel.closeSignInPopup,
    analyticsSharedModel.setParamsForPageOpenEvent,
    resetSkipStaleLivePageOpenAfterSignIn,
  ])

const $liveRegionIdAtSignIn = createStore<number | null>(null).reset([
  signInModel.openSignInPopup,
  signInModel.closeSignInPopup,
])

sample({
  clock: signInModel.signInFx.done,
  source: selectEpgRegionModel.$regionStorage,
  fn: (region) => region?.id ?? null,
  target: $liveRegionIdAtSignIn,
})

export const sendPageOpenFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.PageOpen) =>
    analytix.sendPageOpenEvent(params),
})

sample({
  clock: sendPageOpenFx.failData,
  fn: (e) => e,
  target: logErrorFx,
})

/**
 * Every page_open of a real route goes through here, so the page the user signed in or
 * registered from is reported with the subscriber id of the session it belongs to: the
 * popup closes 25-90 ms before `/users/info` answers (OVP-3119).
 *
 * The sign-in and sign-up popups are virtual pages of a flow the guest started and are
 * reported while the user really is a guest, so the sample that sends them targets the
 * effect directly.
 */
const pageOpened = createEvent<SendEventParams.PageOpen>()

sample({
  clock: afterSubscriberIdSettled(pageOpened),
  target: sendPageOpenFx,
})

// Mirrors `processes/analytics/model` — simple route page_open
sample({
  clock: [
    $pageOpenCanBeSent,
    $route,
    signInModel.$isOpenedSignInPopup,
    signUpModel.$isOpenedSignUpPopup,
    preferencesFeatureModel.$isPreferencesPopupOpened,
  ],
  source: {
    eventCanBeSent: $pageOpenCanBeSent,
    route: $route,
    isSignInPopupOpened: signInModel.$isOpenedSignInPopup,
    isSignUpPopupOpened: signUpModel.$isOpenedSignUpPopup,
    isPreferencesPopupOpened: preferencesFeatureModel.$isPreferencesPopupOpened,
    isNeedAdditionalPageOpenParams: $isNeedAdditionalPageOpenParams,
  },
  filter: ({
    eventCanBeSent,
    route,
    isSignInPopupOpened,
    isSignUpPopupOpened,
    isPreferencesPopupOpened,
    isNeedAdditionalPageOpenParams,
  }) => {
    const isPlayerPage = route?.routeName?.includes(PATH.PLAY)
    return isNeedAdditionalPageOpenParams || isPlayerPage
      ? false
      : Boolean(eventCanBeSent) &&
          Boolean(route) &&
          !isSignInPopupOpened &&
          !isSignUpPopupOpened &&
          !isPreferencesPopupOpened
  },
  fn: ({ route }) =>
    ovpPageOpenPayloadToAnalytix({
      path: route?.routeName || '',
      entityId: String(getIdFromUrl(route?.props?.titleWithId) ?? ''),
      ...getParamsFromUrl(),
    }),
  target: pageOpened,
})

const canSendBeParamsPageOpen = ({
  eventCanBeSent,
  route,
  isSignInPopupOpened,
  isSignUpPopupOpened,
  isPreferencesPopupOpened,
  paramsPageOpenEvent,
  isNeedAdditionalPageOpenParams,
}: {
  eventCanBeSent: boolean
  route: { routeName?: string } | null
  isSignInPopupOpened: boolean
  isSignUpPopupOpened: boolean
  isPreferencesPopupOpened: boolean
  paramsPageOpenEvent: IParamsPageOpenEvent | null
  isNeedAdditionalPageOpenParams: boolean
}) => {
  const isEpgPage = route?.routeName === ROUTE_NAME.LIVE_CATEGORIES
  const isFullscreen =
    // @ts-expect-error TS2551 because Property 'webkitFullscreenElement' does not exist on type 'Document' this fix only Safari #OVP-2982
    Boolean(document.webkitFullscreenElement) || document.fullscreenElement

  if (isEpgPage && isFullscreen) {
    return false
  }
  return (
    isNeedAdditionalPageOpenParams &&
    Boolean(eventCanBeSent) &&
    Boolean(route) &&
    !isSignInPopupOpened &&
    !isSignUpPopupOpened &&
    !isPreferencesPopupOpened &&
    Boolean(paramsPageOpenEvent)
  )
}

// Pages that wait for BE params (`analyticsSharedModel.$paramsPageOpenEvent`)
sample({
  clock: [
    $pageOpenCanBeSent,
    signInModel.$isOpenedSignInPopup,
    signUpModel.$isOpenedSignUpPopup,
    preferencesFeatureModel.$isPreferencesPopupOpened,
    analyticsSharedModel.$paramsPageOpenEvent,
  ],
  source: {
    eventCanBeSent: $pageOpenCanBeSent,
    route: $route,
    isSignInPopupOpened: signInModel.$isOpenedSignInPopup,
    isSignUpPopupOpened: signUpModel.$isOpenedSignUpPopup,
    isPreferencesPopupOpened: preferencesFeatureModel.$isPreferencesPopupOpened,
    paramsPageOpenEvent: analyticsSharedModel.$paramsPageOpenEvent,
    isNeedAdditionalPageOpenParams: $isNeedAdditionalPageOpenParams,
    skipStaleLivePageOpenAfterSignIn: $skipStaleLivePageOpenAfterSignIn,
  },
  filter: ({ skipStaleLivePageOpenAfterSignIn, route, ...source }) => {
    const routeName = route?.routeName
    const isEpgPage = routeName === ROUTE_NAME.LIVE_CATEGORIES
    const isFavoritesPage = routeName === PATH.FAVORITES
    if (skipStaleLivePageOpenAfterSignIn && (isEpgPage || isFavoritesPage)) {
      return false
    }
    return canSendBeParamsPageOpen({ route, ...source })
  },
  fn: ({ route, paramsPageOpenEvent }) =>
    pageOpenFromBeParams(route, paramsPageOpenEvent),
  target: pageOpened,
})

// Compare the profile region to the Live TV region at login — not $regionStorage
// after checkPreferredRegion, which may already have switched.
sample({
  clock: preferencesFeatureModel.$preference,
  source: {
    eventCanBeSent: $pageOpenCanBeSent,
    route: $route,
    isSignInPopupOpened: signInModel.$isOpenedSignInPopup,
    isSignUpPopupOpened: signUpModel.$isOpenedSignUpPopup,
    isPreferencesPopupOpened: preferencesFeatureModel.$isPreferencesPopupOpened,
    paramsPageOpenEvent: analyticsSharedModel.$paramsPageOpenEvent,
    isNeedAdditionalPageOpenParams: $isNeedAdditionalPageOpenParams,
    skipStaleLivePageOpenAfterSignIn: $skipStaleLivePageOpenAfterSignIn,
    liveRegionIdAtSignIn: $liveRegionIdAtSignIn,
    preference: preferencesFeatureModel.$preference,
  },
  filter: ({
    skipStaleLivePageOpenAfterSignIn,
    liveRegionIdAtSignIn,
    preference,
    route,
    ...source
  }) => {
    const isEpgPage = route?.routeName === ROUTE_NAME.LIVE_CATEGORIES
    if (
      !skipStaleLivePageOpenAfterSignIn ||
      !isEpgPage ||
      preference?.id == null ||
      preference.id !== liveRegionIdAtSignIn
    ) {
      return false
    }
    return canSendBeParamsPageOpen({ route, ...source })
  },
  fn: ({ route, paramsPageOpenEvent }) =>
    pageOpenFromBeParams(route, paramsPageOpenEvent),
  target: [pageOpened, resetSkipStaleLivePageOpenAfterSignIn],
})

// Popups as virtual pages
sample({
  clock: [
    signInModel.$isOpenedSignInPopup,
    signUpModel.$isOpenedSignUpPopup,
    preferencesFeatureModel.$isPreferencesPopupOpened,
    $pageOpenCanBeSent,
  ],
  source: {
    eventCanBeSent: $pageOpenCanBeSent,
    isSignInPopupOpened: signInModel.$isOpenedSignInPopup,
    isSignUpPopupOpened: signUpModel.$isOpenedSignUpPopup,
    isPreferencesPopupOpened: preferencesFeatureModel.$isPreferencesPopupOpened,
  },
  filter: ({
    eventCanBeSent,
    isSignInPopupOpened,
    isSignUpPopupOpened,
    isPreferencesPopupOpened,
  }) =>
    (Boolean(eventCanBeSent) &&
      (Boolean(isSignInPopupOpened) || Boolean(isSignUpPopupOpened))) ||
    Boolean(isPreferencesPopupOpened),
  fn: ({
    isSignInPopupOpened,
    isSignUpPopupOpened,
    isPreferencesPopupOpened,
  }) => {
    let path = ''

    if (isSignInPopupOpened) {
      path = PATH.SIGN_IN
    }
    if (isSignUpPopupOpened) {
      path = PATH.SIGN_UP
    }
    if (isPreferencesPopupOpened) {
      path = AnalyticsPathEnum.preferences
    }

    return ovpPageOpenPayloadToAnalytix({ path })
  },
  target: sendPageOpenFx,
})
